import React, { useCallback, useMemo, useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import camelCase from "lodash/camelCase";
import moment from "moment/moment";
import { Trans, useTranslation } from "react-i18next";
import { Row, Col, Input, FormGroup, HelpBlock, ControlLabel, Alert } from "components/bootstrap";
import * as FormsUtils from "util/FormsUtils";
import { describeExpression } from "util/CronUtils";
import { Select, TimeUnitInput, TimezoneSelect } from "components/common";
import { TIME_UNITS } from "components/event-definitions/event-definition-types/FilterForm";
import useUserDateTime from "hooks/useUserDateTime";
import { extractDurationAndUnit } from "components/common/TimeUnitInput";
import { useGetCategories, useGetPriorities } from "security-app/hooks/useAssetsAPI";
import useCanUseAutoCreateInvestigation from "./useCanUseAutoCreateInvestigation";
import AssetEventPreview from "./AssetEventPreview";
const initialRiskThresholdConfig = {
  type: "risk_score_threshold",
  threshold: 75,
  skip_first_run: false,
  auto_create_investigation: false,
  categories: [],
  priorities: []
};
const AUTO_CREATE_INVESTIGATION_HELP = "asset_event_form.auto_create_investigation_help";
const AUTO_CREATE_INVESTIGATION_HELP_WITH_PERMISSION = "asset_event_form.auto_create_investigation_help_with_permission";
const AssetEventForm = ({ eventDefinition, onChange, validation }) => {
  var _a, _b, _c, _d, _e;
  const { t } = useTranslation("enterprise_security_assets");
  const { userTimezone } = useUserDateTime();
  const [cronDescription, setCronDescription] = useState(
    eventDefinition.config.cron_expression ? describeExpression(eventDefinition.config.cron_expression) : ""
  );
  const { categories } = useGetCategories();
  const { priorities } = useGetPriorities();
  const canEnableAutoInvestigations = useCanUseAutoCreateInvestigation();
  const config = eventDefinition.config;
  const subtypeConfig = config.config || initialRiskThresholdConfig;
  const { execute_every_ms: executeEveryMs, search_within_ms: searchWithinMs } = config;
  const searchWithin = extractDurationAndUnit(searchWithinMs, TIME_UNITS);
  const executeEvery = extractDurationAndUnit(executeEveryMs, TIME_UNITS);
  const [searchWithinMsUnit, setSearchWithinMsUnit] = useState(searchWithin.unit);
  const [executeEveryMsUnit, setExecuteEveryMsUnit] = useState(executeEvery.unit);
  const [searchWithinMsDuration, setSearchWithinMsDuration] = useState(searchWithin.duration);
  const [executeEveryMsDuration, setExecuteEveryMsDuration] = useState(executeEvery.duration);
  const autoCreateInvestigationHelpText = canEnableAutoInvestigations ? t(AUTO_CREATE_INVESTIGATION_HELP) : t(AUTO_CREATE_INVESTIGATION_HELP_WITH_PERMISSION);
  const categoryOptions = useMemo(
    () => categories.map((category) => ({
      value: category.category,
      label: category.category
    })),
    [categories]
  );
  const priorityOptions = useMemo(
    () => priorities.map((priority) => ({
      value: priority.priority.toString(),
      label: priority.text
    })),
    [priorities]
  );
  const propagateConfigChange = useCallback(
    (nextConfig) => {
      onChange("config", nextConfig);
    },
    [onChange]
  );
  const handleConfigChange = (event) => {
    const { name } = event.target;
    const value = FormsUtils.getValueFromInput(event.target);
    const nextConfig = cloneDeep(config);
    nextConfig[name] = value;
    propagateConfigChange(nextConfig);
  };
  const handleSubTypeConfigChange = (event) => {
    const { name } = event.target;
    const value = FormsUtils.getValueFromInput(event.target);
    const nextConfig = cloneDeep(config);
    if (!nextConfig.config) {
      nextConfig.config = cloneDeep(subtypeConfig);
    }
    nextConfig.config[name] = value;
    propagateConfigChange(nextConfig);
  };
  const handleCategoriesChange = (selectedCategories) => {
    const nextConfig = cloneDeep(config);
    if (!nextConfig.config) {
      nextConfig.config = cloneDeep(subtypeConfig);
    }
    nextConfig.config.categories = selectedCategories.length ? selectedCategories : void 0;
    propagateConfigChange(nextConfig);
  };
  const handlePrioritiesChange = (selectedPriorities) => {
    const nextConfig = cloneDeep(config);
    if (!nextConfig.config) {
      nextConfig.config = cloneDeep(subtypeConfig);
    }
    nextConfig.config.priorities = selectedPriorities.length ? selectedPriorities : void 0;
    propagateConfigChange(nextConfig);
  };
  const handleTimeRangeChange = (fieldName) => (nextValue, nextUnit) => {
    const durationInMs = moment.duration(Math.max(nextValue, 1), nextUnit).asMilliseconds();
    const nextConfig = cloneDeep(config);
    nextConfig[fieldName] = durationInMs;
    propagateConfigChange(nextConfig);
    const stateFieldName = camelCase(fieldName);
    if (stateFieldName === "searchWithinMs") {
      setSearchWithinMsDuration(nextValue);
      setSearchWithinMsUnit(nextUnit);
      return;
    }
    setExecuteEveryMsDuration(nextValue);
    setExecuteEveryMsUnit(nextUnit);
  };
  const handleCronTimezoneChange = (tz) => {
    const nextConfig = cloneDeep(config);
    nextConfig.cron_timezone = tz;
    propagateConfigChange(nextConfig);
  };
  const handleUseCronSchedulingChange = (event) => {
    const { name } = event.target;
    const value = FormsUtils.getValueFromInput(event.target);
    const nextConfig = cloneDeep(config);
    nextConfig[name] = value;
    if (value) {
      nextConfig.cron_expression = "";
      nextConfig.cron_timezone = userTimezone;
    } else {
      nextConfig.cron_expression = null;
      nextConfig.cron_timezone = null;
    }
    propagateConfigChange(nextConfig);
  };
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 7, lg: 6 }, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "risk-score-threshold",
      name: "threshold",
      label: t("asset_event_form.risk_score_threshold"),
      type: "number",
      min: 1,
      max: 100,
      value: subtypeConfig.threshold,
      onChange: handleSubTypeConfigChange,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, null, t("asset_event_form.asset_categories")), /* @__PURE__ */ React.createElement(
    Select,
    {
      id: "asset-event-categories",
      name: "categories",
      placeholder: t("asset_event_form.add_or_select_categories"),
      multi: true,
      allowCreate: true,
      menuPlacement: "bottom",
      maxMenuHeight: 200,
      value: subtypeConfig.categories && subtypeConfig.categories.length > 0 ? subtypeConfig.categories.join(",") : null,
      onChange: (newValue) => handleCategoriesChange(newValue ? newValue.split(",") : []),
      options: categoryOptions
    }
  )), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, null, t("asset_event_form.asset_priorities")), /* @__PURE__ */ React.createElement(
    Select,
    {
      id: "asset-event-priorities",
      name: "priorities",
      placeholder: t("asset_event_form.select_priorities"),
      menuPlacement: "bottom",
      maxMenuHeight: 200,
      multi: true,
      value: subtypeConfig.priorities && subtypeConfig.priorities.length > 0 ? subtypeConfig.priorities.map((priority) => priority.toString()).join(",") : null,
      onChange: (newValue) => handlePrioritiesChange(
        newValue ? newValue.split(",").filter((value) => value !== "").map((value) => Number(value)) : []
      ),
      options: priorityOptions
    }
  )), !eventDefinition.id && /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "skip-first-run-checkbox",
      type: "checkbox",
      name: "skip_first_run",
      label: t("asset_event_form.skip_first_run"),
      help: t("asset_event_form.skip_first_run_help"),
      checked: subtypeConfig.skip_first_run,
      onChange: handleSubTypeConfigChange,
      disabled: !!eventDefinition.id
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "auto-create-investigation-checkbox",
      type: "checkbox",
      name: "auto_create_investigation",
      label: t("asset_event_form.auto_create_investigation"),
      help: autoCreateInvestigationHelpText,
      checked: (_a = subtypeConfig.auto_create_investigation) != null ? _a : false,
      onChange: handleSubTypeConfigChange,
      disabled: !canEnableAutoInvestigations
    }
  ), !canEnableAutoInvestigations && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "warning" }, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "asset_event_form.auto_create_investigation_requirements",
      values: {
        createPermission: "investigations:create",
        editPermission: "investigations:edit",
        stream: "All events"
      },
      components: { code: /* @__PURE__ */ React.createElement("code", null) }
    }
  )), subtypeConfig.type !== "risk_score_threshold" && /* @__PURE__ */ React.createElement(FormGroup, { controlId: "search-within", validationState: validation.errors.search_within_ms ? "error" : null }, /* @__PURE__ */ React.createElement(
    TimeUnitInput,
    {
      label: t("asset_event_form.search_within_last"),
      update: handleTimeRangeChange("search_within_ms"),
      value: searchWithinMsDuration,
      unit: searchWithinMsUnit,
      units: TIME_UNITS,
      clearable: true,
      required: true
    }
  ), (validation == null ? void 0 : validation.errors.search_within_ms) && /* @__PURE__ */ React.createElement(HelpBlock, null, validation.errors.search_within_ms[0])), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "is-cron-checkbox",
      type: "checkbox",
      name: "use_cron_scheduling",
      label: t("asset_event_form.use_cron_scheduling"),
      help: t("asset_event_form.use_cron_scheduling_help"),
      checked: (_b = config.use_cron_scheduling) != null ? _b : false,
      onChange: handleUseCronSchedulingChange
    }
  ), config.use_cron_scheduling ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(FormGroup, { controlId: "cron-expression", validationState: validation.errors.cron_expression ? "error" : null }, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "cron-expression",
      name: "cron_expression",
      label: t("asset_event_form.cron_expression"),
      type: "text",
      help: /* @__PURE__ */ React.createElement("span", null, cronDescription || t("asset_event_form.cron_expression_help")),
      value: (_c = config.cron_expression) != null ? _c : "",
      onBlur: () => setCronDescription(describeExpression(config.cron_expression)),
      onChange: handleConfigChange
    }
  ), validation.errors.cron_expression && /* @__PURE__ */ React.createElement(HelpBlock, null, validation.errors.cron_expression[0])), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, null, t("asset_event_form.cron_time_zone")), /* @__PURE__ */ React.createElement(
    TimezoneSelect,
    {
      value: (_d = config.cron_timezone) != null ? _d : userTimezone,
      name: "cron_timezone",
      clearable: false,
      onChange: handleCronTimezoneChange
    }
  ))) : /* @__PURE__ */ React.createElement(FormGroup, { controlId: "execute-every", validationState: validation.errors.execute_every_ms ? "error" : null }, /* @__PURE__ */ React.createElement(
    TimeUnitInput,
    {
      label: t("asset_event_form.execute_search_every"),
      update: handleTimeRangeChange("execute_every_ms"),
      value: executeEveryMsDuration,
      unit: executeEveryMsUnit,
      units: TIME_UNITS,
      clearable: true,
      required: true
    }
  ), validation.errors.execute_every_ms && /* @__PURE__ */ React.createElement(HelpBlock, null, validation.errors.execute_every_ms[0])), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "schedule-checkbox",
      type: "checkbox",
      name: "_is_scheduled",
      label: t("asset_event_form.enable"),
      help: t("asset_event_form.enable_help"),
      checked: (_e = config._is_scheduled) != null ? _e : true,
      onChange: handleConfigChange
    }
  )), /* @__PURE__ */ React.createElement(Col, { md: 5, lg: 6 }, /* @__PURE__ */ React.createElement(AssetEventPreview, { config })));
};
AssetEventForm.defaultConfig = {
  search_within_ms: 0,
  execute_every_ms: 5 * 60 * 1e3,
  _is_scheduled: true,
  use_cron_scheduling: false,
  config: initialRiskThresholdConfig
};
export default AssetEventForm;
